import React from 'react';
import MarkdownText from '../MarkdownText';

// Universal Card — the default fallback card when a structured value
// carries no `kind` (or an unregistered one). Domain-blind by design:
// helpers populate {text, image_url?, actions?} via
// `aveq_link.cards.build_card` and the card renders without knowing
// anything about the helper's domain.
//
// Expected structured fields:
//   text       — markdown body (required)
//   image_url  — optional; rendered above the body, hidden on 404
//   image_fit  — optional; 'contain' shows the whole image instead of the cropped banner
//   actions    — optional list of action_spec dicts; up to 3 buttons
//                rendered right-aligned, primary (index 0) rightmost.
//   action     — legacy singular; treated as `actions: [action]` if
//                actions is absent.
//
// Filename retained from when this was GenericCard; the concept is the
// Universal Card per shasta-notes/decisions/2026-05-23-universal-card.md.
const BANNER = {
  width: '100%', maxHeight: '200px', aspectRatio: '16 / 9',
  objectFit: 'cover', borderRadius: '6px', display: 'block',
};
// A photo whose subject the banner crop would cut (a dog's head, a whole body).
const WHOLE_IMAGE = {
  width: '100%', maxHeight: '360px', objectFit: 'contain', borderRadius: '6px',
  display: 'block', background: '#e9ecef',
};

export default function GenericCard({ structured, onAction }) {
  // Normalize legacy singular `action` into the `actions[]` shape.
  // Defensive slice(0, 3): the aveq_link.cards.build_card validator
  // enforces the same limit at emission time, but trim again here so
  // a hand-constructed payload that bypasses the builder can't paint
  // an overflowing row.
  const actions = (structured.actions ?? (structured.action ? [structured.action] : []))
    .slice(0, 3);

  return (
    <div style={{
      marginBottom: '10px', padding: '10px', background: '#f8f9fa',
      borderRadius: '8px', border: '1px solid #e9ecef',
      display: 'flex', flexDirection: 'column', gap: '8px',
    }}>
      {structured.image_url && (
        // Wrapper hides itself on 404 so the card reflows cleanly.
        // CSS bounds prevent visual harm regardless of source size;
        // the documented convention asks helpers to pre-size assets
        // (~200 KB, ~800×600) to avoid wasted bandwidth.
        <div>
          <img
            src={structured.image_url}
            alt=""
            loading="lazy"
            decoding="async"
            style={structured.image_fit === 'contain' ? WHOLE_IMAGE : BANNER}
            onError={(e) => {
              const wrapper = e.currentTarget.parentElement;
              if (wrapper) wrapper.style.display = 'none';
            }}
          />
        </div>
      )}
      <div style={{ fontSize: '0.9rem', fontFamily: '"IBM Plex Mono", monospace' }}>
        <MarkdownText text={structured.text} />
      </div>
      {actions.length > 0 && (
        // row-reverse so DOM order [primary, secondary, tertiary] paints
        // primary at the right edge. Wraps on narrow viewports; gap keeps
        // separation when the row breaks across lines.
        <div style={{
          display: 'flex', flexDirection: 'row-reverse', flexWrap: 'wrap',
          gap: '6px', justifyContent: 'flex-start',
        }}>
          {actions.map((action, i) => (
            <button
              key={i}
              onClick={() => onAction(action, structured)}
              style={{
                fontSize: '0.75rem', fontWeight: 700, padding: '4px 12px',
                border: '1px solid #0084ff', borderRadius: '14px',
                background: 'transparent', color: '#0084ff', cursor: 'pointer',
              }}
            >
              {action.label || 'Apply'}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
